// MOVER UNA POLIZA A OTRO CLIENTE.
//
// Maritza Mendoza, 2026-09-10, por soporte: una poliza aparecio en la ficha de
// OTRO cliente, "y es el 2do caso similar". Hasta ahora el unico arreglo era
// borrarla y teclearla de nuevo — y con ella se iban sus pagos, sus notas y sus
// comisiones. De ahi que cada caso acabara en soporte.
//
// EL MODAL VIVE EN SU PROPIO ARCHIVO porque se abre desde DOS sitios: la ficha
// del cliente (donde se descubre el error) y la pantalla de Polizas. Escribirlo
// dos veces es como se llega a que un sitio avise de lo que resetea y el otro no.
//
// LO QUE ESTA PANTALLA TIENE QUE HACER BIEN, Y ES SU RAZON DE SER: decir ANTES
// de mover lo que se va a resetear. Las personas aseguradas de una poliza no se
// guardan por nombre sino como claves del hogar del cliente ("primary",
// "spouse", el id de cada dependiente), asi que al cambiar de cliente dejan de
// significar lo que decian y hay que volver a marcarlas. Callarlo seria que la
// agente lo descubriera despues, mirando una poliza que dice asegurar a la
// familia de otro (regla de oro 20).

import { useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
// El armazon del modal es el `Modal` de siempre (con su portal a <body>, su
// cabecera y su cierre): escribir uno a mano fue como se colo `dkt-modal-back`,
// una clase de CSS que NO EXISTE, y eso no lo ve el build.
// (La primera version de esta nota acusaba tambien a `dkt-btn`; esa si existe,
// en dkt.css. Se corrige aqui porque una advertencia que nombra mal lo que
// paso manda a la siguiente sesion a buscar donde no es.)
import { Modal, ClientSearchSelect } from './ui.jsx';
import { movePolicyToClient } from '../api.js';
import { toast } from './Toast.jsx';
// La cuenta de lo que se resetea vive suelta, sin importar nada: asi la prueba
// del servidor puede comprobar que dice lo MISMO que se aplica.
import { loQueSeResetea } from '../moverPoliza.js';

/** El nombre de un cliente tal y como se lee. */
const nombreDe = (c) => `${c?.firstName || ''} ${c?.lastName || ''}`.trim();

export function MoverPolizaModal({ policy, clients = [], clienteActual, lang = 'es', onClose, onMoved }) {
  const es = lang === 'es';
  const [destino, setDestino] = useState('');
  const [moviendo, setMoviendo] = useState(false);

  // El cliente que ya la tiene no se ofrece: elegirlo solo puede acabar en un
  // error, y una opcion que nunca vale no deberia estar en la lista.
  const candidatos = useMemo(
    () => (clients || []).filter((c) => c && c.id && c.id !== policy?.clientId),
    [clients, policy && policy.clientId],
  );
  const elegido = candidatos.find((c) => c.id === destino) || null;
  const resetea = loQueSeResetea(policy);

  const mover = async () => {
    if (!destino || moviendo) return;
    setMoviendo(true);
    try {
      const { policy: movida, movido } = await movePolicyToClient(policy.id, destino);
      // El resumen sale de lo que CONTESTO el servidor, no de lo que se pensaba
      // mandar (regla 142). Si el servidor movio 3 comisiones, dice 3.
      const piezas = [];
      if (movido.comisiones) piezas.push(es ? `${movido.comisiones} comisión(es)` : `${movido.comisiones} commission(s)`);
      if (movido.citas) piezas.push(es ? `${movido.citas} cita(s)` : `${movido.citas} appointment(s)`);
      if (movido.avisos) piezas.push(es ? `${movido.avisos} aviso(s)` : `${movido.avisos} alert(s)`);
      toast.success(
        (es ? `Póliza movida a ${nombreDe(elegido)}` : `Policy moved to ${nombreDe(elegido)}`)
        + (piezas.length ? (es ? `, con ${piezas.join(', ')}` : `, with ${piezas.join(', ')}`) : ''),
      );
      if (typeof onMoved === 'function') onMoved(movida, movido, elegido);
      onClose();
    } catch (e) {
      // El motivo se enseña tal cual: "no se pudo mover" a secas obliga a
      // escribir a soporte para saber si fue permiso, alcance o la póliza.
      toast.error(e.message || (es ? 'No se pudo mover la póliza' : 'Could not move the policy'));
    } finally {
      setMoviendo(false);
    }
  };

  if (!policy) return null;

  return (
    <Modal title={es ? 'Mover la póliza a otro cliente' : 'Move policy to another client'} onClose={onClose}>
        {/* QUE poliza es. Sin esto, con varias pólizas abiertas no hay forma de
            estar seguro de cuál se está moviendo. */}
        <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.9rem', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--c-text,#e8f4ff)' }}>{policy.carrier || '—'}</strong>
          {policy.plan ? ` · ${policy.plan}` : ''}
          {policy.policyNumber ? ` · #${policy.policyNumber}` : ''}
          <br />
          {es ? 'Ahora está en' : 'Currently on'} <strong style={{ color: 'var(--c-text,#e8f4ff)' }}>{nombreDe(clienteActual) || '—'}</strong>
        </div>

        <label className="dkt-input-lbl">{es ? 'Mover a' : 'Move to'}</label>
        <ClientSearchSelect
          clients={candidatos}
          value={destino}
          onChange={(e) => setDestino(e && e.target ? e.target.value : e)}
          noneLabel={es ? '— elegir cliente —' : '— pick a client —'}
          lang={lang} />

        {/* LO QUE SE CONSERVA, dicho en voz alta: es la razón por la que mover
            es mejor que borrar y volver a crear, y no se ve por ningún lado. */}
        <div style={{
          marginTop: '.9rem', padding: '.6rem .7rem', borderRadius: 8, fontSize: '.72rem', lineHeight: 1.55,
          background: 'rgba(0,212,255,.07)', border: '1px solid rgba(0,212,255,.25)', color: 'var(--c-text2,#7aa3c0)',
        }}>
          {es
            ? 'Se lleva todo su historial: pagos del mes, notas, comisiones, recordatorios y avisos.'
            : 'It keeps its whole history: monthly payments, notes, commissions, reminders and alerts.'}
        </div>

        {/* Y LO QUE NO. Solo cuando de verdad hay algo que resetear: un aviso
            que sale siempre deja de leerse. */}
        {(resetea.asegurados > 0 || resetea.numeros > 0) && (
          <div style={{
            marginTop: '.5rem', padding: '.6rem .7rem', borderRadius: 8, fontSize: '.72rem', lineHeight: 1.55,
            background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.35)', color: 'var(--c-amber,#f59e0b)',
          }}>
            {es
              ? `Las personas aseguradas se vuelven a marcar: quedará solo el titular${resetea.asegurados ? ` (se desmarcan ${resetea.asegurados})` : ''}${resetea.numeros ? ` y se quitan ${resetea.numeros} Member ID que eran de personas del otro hogar` : ''}. Se guardan en el historial de la póliza.`
              : `The insured people get reset to the policy holder only${resetea.asegurados ? ` (${resetea.asegurados} unticked)` : ''}${resetea.numeros ? ` and ${resetea.numeros} Member ID(s) belonging to the other household are removed` : ''}. They are kept in the policy history.`}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1.1rem' }}>
          <button type="button" className="dkt-btn-g" onClick={onClose} disabled={moviendo}>
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button type="button" className="dkt-btn-p" onClick={mover} disabled={!destino || moviendo}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: (!destino || moviendo) ? 0.5 : 1 }}>
            <ArrowRight size={14} />
            {moviendo ? (es ? 'Moviendo…' : 'Moving…') : (es ? 'Mover' : 'Move')}
          </button>
        </div>
    </Modal>
  );
}
